import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminSideMenu from "../components/AdminSideMenu";
import AdminTopMenu from "../components/AdminTopMenu/AdminTopMenu";
import { useRooms } from "../contexts/RoomContext";
import styles from "./JoinInfoScreen.module.css";

// NEW: pdf + qr
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
const JoinInfoScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const roomId = id ? parseInt(id) : undefined;
  const { currentRoom, loading, error, fetchRoomById, editRoom } = useRooms();

  const [speakerUrl, setSpeakerUrl] = useState("");
  const [participantUrl, setParticipantUrl] = useState("");
  const [isEditingRedirect, setIsEditingRedirect] = useState(false);
  const [redirectUrlInput, setRedirectUrlInput] = useState("");
  const [savingRedirect, setSavingRedirect] = useState(false);

  const onBasicSetupClick = useCallback(() => {
    navigate(roomId ? `/basic-setup/${roomId}` : "/basic-setup");
  }, [navigate, roomId]);

  const onAdvancedSetupClick = useCallback(() => {
    navigate(roomId ? `/advanced-setup/${roomId}` : "/advanced-setup");
  }, [navigate, roomId]);

  const onRoomsListClick = useCallback(() => {
    navigate("/rooms");
  }, [navigate]);

  useEffect(() => {
    if (roomId) fetchRoomById(roomId);
  }, [roomId, fetchRoomById]);

  useEffect(() => {
    if (currentRoom?.id) {
      const speakerBaseUrl =
        process.env.REACT_APP_SPEAKER_URL || "http://localhost:3003";
      const participantBaseUrl =
        process.env.REACT_APP_PARTICIPANT_URL || "http://localhost:3000";

      setSpeakerUrl(
        `${speakerBaseUrl}/speaker/${currentRoom.roomId}${
          currentRoom.speakerPassword
            ? `?pwd=${currentRoom.speakerPassword}`
            : ""
        }`
      );
      setParticipantUrl(
        `${participantBaseUrl}/listener/${currentRoom.roomId}${
          currentRoom.listenersPassword
            ? `?pwd=${currentRoom.listenersPassword}`
            : ""
        }`
      );
    }
  }, [currentRoom]);

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => alert(`${type} copied to clipboard`))
      .catch((err) => {
        console.error(`Could not copy ${type}: `, err);
        alert(`Failed to copy ${type}.`);
      });
  };

  const saveListenerRedirect = async (value: string | null): Promise<void> => {
    if (!currentRoom?.id) return;
    setSavingRedirect(true);
    try {
      await editRoom(currentRoom.id, { listenerRedirectUrl: value });
      await fetchRoomById(currentRoom.id);
      setIsEditingRedirect(false);
    } catch (err: any) {
      alert(
        err?.response?.data?.details?.[0]?.msg ||
          err?.response?.data?.error ||
          "Failed to save redirect URL."
      );
    } finally {
      setSavingRedirect(false);
    }
  };

  const shareViaEmail = (role: string, url: string) => {
    const subject = `Invitation to Manaber Translation Room: ${
      currentRoom?.name || "Room"
    } - ${role}`;
    const body = `Hello,\n\nYou are invited to join the Manaber translation room "${
      currentRoom?.name || "Room"
    }" as a ${role.toLowerCase()}:\n\n${url}\n\nRegards,\nThe Manaber Team`;
    window.location.href = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  const getQrCodeUrl = (url: string) => {
    if (!url) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
      url
    )}`;
  };
  
  // Build the PDF and return as File (no share/download here)
  type Role = "speaker" | "listener";

  // Build a role-specific PDF and return as File (no share/download here)
  const createJoinPdfFile = async ({
    roomName,
    spkUrl,
    lstUrl,
    role, // NEW
  }: {
    roomName: string;
    spkUrl: string;
    lstUrl: string;
    role: Role; // NEW
  }) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" }); // 595 x 842
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
  
    const brand = {
      primary: [255, 255, 255] as [number, number, number],
      dark: [30, 30, 30] as [number, number, number],
      gray: [7, 113, 105] as [number, number, number],
      lightBg: [21, 103, 81] as [number, number, number],
      line: [230, 235, 235] as [number, number, number],
    };
  
    const EMAIL = process.env.MANABER_SUPPORT_EMAIL || "support@manaber.ai";
    const WEBSITE = process.env.MANABER_WEBSITE_URL || "https://www.manaber.ai";
    const APPSTORE_URL =
      process.env.MANABER_APPSTORE_URL ||
      "https://apps.apple.com/ae/app/manaber/id6746405549";
    const PLAYSTORE_URL =
      process.env.MANABER_PLAYSTORE_URL ||
      "https://play.google.com/store/apps/details?id=com.manaberai";
    const LOGO_PATH = process.env.MANABER_LOGO_PATH || "/brand-logo.png";
    const APPSTORE_BADGE_PATH =
      process.env.REACT_APP_APPSTORE_BADGE_PATH || "/appstore-badge.png";
    const PLAYSTORE_BADGE_PATH =
      process.env.REACT_APP_PLAYSTORE_BADGE_PATH || "/playstore-badge.png";
  
    const toDataURL = async (path: string) => {
      const res = await fetch(path, { cache: "no-store" });
      const blob = await res.blob();
      return await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.readAsDataURL(blob);
      });
    };
  
    let logoDataUrl: string | null = null;
    let appStoreBadge: string | null = null;
    let playStoreBadge: string | null = null;
    try {
      [logoDataUrl, appStoreBadge, playStoreBadge] = await Promise.all([
        toDataURL(LOGO_PATH),
        toDataURL(APPSTORE_BADGE_PATH),
        toDataURL(PLAYSTORE_BADGE_PATH),
      ]);
    } catch {}
  
    // Header (role-specific subtitle)
    doc.setFillColor(...brand.lightBg);
    doc.rect(0, 0, pageW, 160, "F");
  
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...brand.primary);
    doc.setFontSize(22);
    doc.text(
      `Manaber | ${role === "speaker" ? "Speaker" : "Listener"} Join Information`,
      40,
      60
    );
  
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...brand.primary);
    doc.setFontSize(12);
    doc.text(`Room: ${roomName}`, 40, 88);
  
    if (logoDataUrl) {
      const logoW = 80;
      const logoH = 70;
      doc.addImage(logoDataUrl, "PNG", pageW - logoW - 40, 40, logoW, logoH);
    }
  
    // Card
    const cardX = 40;
    const cardY = 120;
    const cardW = pageW - 80;
    const cardH = pageH - cardY - 110;
    doc.setDrawColor(...brand.line);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(cardX, cardY, cardW, cardH, 12, 12, "FD");
  
    const innerX = cardX + 24;
    let cursorY = cardY + 40;
  
    const sectionTitle = (txt: string) => {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...brand.dark);
      doc.setFontSize(16);
      doc.text(txt, innerX, cursorY);
      cursorY += 10;
    };
  
    const drawUrl = (url: string, marginTop = 50, label: string) => {
    
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...brand.dark);
      doc.setFontSize(12);
    
      const textW = doc.getTextWidth(label);
      const textX = cardX + (cardW - textW) / 2;
      doc.text(label, textX, cursorY + 20); // small top margin for text
      
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...brand.gray);
      doc.setFontSize(20);
    
      // apply margin before drawing
      cursorY += marginTop;
    
      const maxWidth = cardW - 48;
      const lines: string[] = doc.splitTextToSize(url, maxWidth) as string[];
      const lineHeight = 18; // you can bump this since font size is bigger
    
      lines.forEach((line: string, i: number) => {
        const textW = doc.getTextWidth(line);
        const x = cardX + (cardW - textW) / 2;
        doc.text(line, x, cursorY + i * lineHeight);
      });
    
      cursorY += lines.length * lineHeight;
    };
    
  
    const drawQr = async (url: string, label: string) => {
      const qrSize = 250;
    
      // --- draw text first ---
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...brand.dark);
      doc.setFontSize(12);
    
      const textW = doc.getTextWidth(label);
      const textX = cardX + (cardW - textW) / 2;
      doc.text(label, textX, cursorY + 20); // small top margin for text
    
      // --- then QR ---
      const qr = await QRCode.toDataURL(url, { width: qrSize, margin: 1 });
      const qrX = cardX + (cardW - qrSize) / 2;
      const qrY = cursorY + 40; // place QR below text
      doc.addImage(qr, "PNG", qrX, qrY, qrSize, qrSize);
    
      // advance cursor after QR
      cursorY = qrY + qrSize + 30; // add some bottom margin
    };
    
  
    // Only draw the selected role
    if (role === "speaker") {
      sectionTitle("Speaker");
      await drawQr(spkUrl, "Scan to join as Speaker");
      drawUrl(spkUrl, 50 , "or Click Link to Join");
    } else {
      sectionTitle("Listener");
      await drawQr(lstUrl, "Scan to join as Listener");
      drawUrl(lstUrl,50 , "or Click Link to Join");
    }
  
    // Rounded badge helper (canvas)
    const drawRoundedImage = async (
      dataUrl: string,
      x: number,
      y: number,
      w: number,
      h: number,
      radius = 8
    ) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = dataUrl;
      await img.decode();
  
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(w);
      canvas.height = Math.round(h);
      const ctx = canvas.getContext("2d")!;
      const r = Math.min(radius, w / 2, h / 2);
  
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.lineTo(w - r, 0);
      ctx.quadraticCurveTo(w, 0, w, r);
      ctx.lineTo(w, h - r);
      ctx.quadraticCurveTo(w, h, w - r, h);
      ctx.lineTo(r, h);
      ctx.quadraticCurveTo(0, h, 0, h - r);
      ctx.lineTo(0, r);
      ctx.quadraticCurveTo(0, 0, r, 0);
      ctx.closePath();
      ctx.clip();
  
      ctx.drawImage(img, 0, 0, w, h);
  
      const roundedDataUrl = canvas.toDataURL("image/png");
      doc.addImage(roundedDataUrl, "PNG", x, y, w, h);
    };
  
    // Footer
    const footerY = cardY + cardH + 18;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...brand.dark);
    const webText = `Website: ${WEBSITE.replace(/^https?:\/\//, "")}`;
    const emailText = `Email: ${EMAIL}`;
  
    doc.text(webText, 40, footerY + 36);
    doc.text(emailText, 40, footerY + 54);
  
    const webW = doc.getTextWidth(webText);
    doc.link(40, footerY + 26, webW, 14, { url: WEBSITE });
    const emailW = doc.getTextWidth(emailText);
    doc.link(40, footerY + 44, emailW, 14, { url: `mailto:${EMAIL}` });
  
    const badgeW = 110;
    const badgeH = 34;
    const gap = 10;
    let badgeX = pageW - badgeW - 40;
    const badgeY = footerY;
    if (appStoreBadge) {
      await drawRoundedImage(appStoreBadge, badgeX, badgeY, badgeW, badgeH, 8);
      doc.link(badgeX, badgeY, badgeW, badgeH, { url: APPSTORE_URL });
    }
    if (playStoreBadge) {
      badgeX -= badgeW + gap;
      await drawRoundedImage(playStoreBadge, badgeX, badgeY, badgeW, badgeH, 8);
      doc.link(badgeX, badgeY, badgeW, badgeH, { url: PLAYSTORE_URL });
    }
  
    // Return role-specific file
    const blob = doc.output("blob");
    const safeRoom = (roomName || "room").replace(/[^\w\-]+/g, "_");
    const fileName = `Manaber-${role}-${safeRoom}.pdf`;
    const file = new File([blob], fileName, { type: "application/pdf" });
    return { file, blob, fileName };
  };
  

// Download: force-save
const onDownloadPdf = async (role: Role) => {
  if (!speakerUrl || !participantUrl) {
    alert("Join links are not ready yet.");
    return;
  }
  const { blob, fileName } = await createJoinPdfFile({
    roomName: currentRoom?.name || String(currentRoom?.roomId || roomId || ""),
    spkUrl: speakerUrl,
    lstUrl: participantUrl,
    role,
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

const [shareFallbackOpen, setShareFallbackOpen] = useState(false);

const onSharePdf = async (role: Role) => {
  if (!speakerUrl || !participantUrl) {
    alert("Join links are not ready yet.");
    return;
  }
  const { file } = await createJoinPdfFile({
    roomName: currentRoom?.name || String(currentRoom?.roomId || roomId || ""),
    spkUrl: speakerUrl,
    lstUrl: participantUrl,
    role,
  });

  const navAny = navigator as any;
  const canShareFiles =
    typeof navAny.canShare === "function" && navAny.canShare({ files: [file] });

  if (typeof navAny.share === "function" && canShareFiles) {
    try {
      await navAny.share({
        title: `Manaber ${role === "speaker" ? "Speaker" : "Listener"} Join Info`,
        text: `Join details for ${currentRoom?.name || roomId || "room"}`,
        files: [file],
      });
      return;
    } catch {
      setShareFallbackOpen(true);
      return;
    }
  }
  setShareFallbackOpen(true);
};


  const renderInfoSection = (role: "Speaker" | "Listener") => {
    const url = role === "Speaker" ? speakerUrl : participantUrl;
    const password =
      role === "Speaker"
        ? currentRoom?.speakerPassword
        : currentRoom?.listenersPassword;

    return (
      <div className={styles.infoSection}>
        <h2 className={styles.infoSectionTitle}>{role} Information</h2>

        <div className={styles.detailItem}>
          <label className={styles.detailLabel}>Join URL</label>
          <input
            type="text"
            className={styles.joinInfoInput}
            value={url}
            readOnly
          />
        </div>

        {role === "Listener" && (
          <div className={styles.detailItem}>
            <label className={styles.detailLabel}>Redirect URL</label>
            {isEditingRedirect ? (
              <>
                <input
                  type="url"
                  className={styles.joinInfoInput}
                  value={redirectUrlInput}
                  onChange={(e) => setRedirectUrlInput(e.target.value)}
                  placeholder="https://..."
                />
                <div className={styles.actionsContainer}>
                  <button
                    className={`${styles.actionButton} ${styles.joinButton}`}
                    onClick={() => saveListenerRedirect(redirectUrlInput.trim() || null)}
                    disabled={savingRedirect}
                  >
                    Save
                  </button>
                  <button
                    className={`${styles.actionButton} ${styles.copyButton}`}
                    onClick={() => setIsEditingRedirect(false)}
                    disabled={savingRedirect}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <input
                  type="text"
                  className={styles.joinInfoInput}
                  value={currentRoom?.listenerRedirectUrl || "Not set"}
                  readOnly
                />
                <div className={styles.actionsContainer}>
                  <button
                    className={`${styles.actionButton} ${styles.copyButton}`}
                    onClick={() => {
                      setRedirectUrlInput(currentRoom?.listenerRedirectUrl || "");
                      setIsEditingRedirect(true);
                    }}
                  >
                    Edit
                  </button>
                  {currentRoom?.listenerRedirectUrl && (
                    <button
                      className={`${styles.actionButton} ${styles.copyButton}`}
                      onClick={() => saveListenerRedirect(null)}
                      disabled={savingRedirect}
                    >
                      Clear Redirect
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {password && (
          <div className={styles.detailItem}>
            <label className={styles.detailLabel}>Password</label>
            <div className={styles.detailValue}>{password}</div>
          </div>
        )}

        {url && (
          <img
            className={styles.qrCodeImage}
            src={getQrCodeUrl(url)}
            alt={`${role} QR Code`}
          />
        )}

        <div className={styles.actionsContainer}>
          <button
            className={`${styles.actionButton} ${styles.joinButton}`}
            onClick={() => window.open(url, "_blank")}
            disabled={!url}
          >
            Join as {role}
          </button>
          <button
            className={`${styles.actionButton} ${styles.copyButton}`}
            onClick={() => copyToClipboard(url, `${role} URL`)}
            disabled={!url}
          >
            Copy URL
          </button>
          {url && (
            <a
              href={getQrCodeUrl(url)}
              download={`${role.toLowerCase()}-qr-${roomId || "room"}.png`}
              className={`${styles.actionButton} ${styles.qrButton}`}
            >
              Download QR Code
            </a>
          )}
          
        </div>
      </div>
    );
  };

  return (
    <div>
      <AdminSideMenu />
      <AdminTopMenu />
      <div className={styles.joinInfoScreenWrapper}>
        <div className={styles.mainContentContainer}>
          <div className={styles.tabsContainer}>
            <div className={styles.tabItem} onClick={onBasicSetupClick}>
              Basic setup
            </div>
            <div className={styles.tabItem} onClick={onAdvancedSetupClick}>
              Advanced settings
            </div>
            <div className={`${styles.tabItem} ${styles.activeTab}`}>
              Join Info
            </div>

            <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
  <button
    className={styles.downloadShareBtn}
    onClick={() => onDownloadPdf("speaker")}
    disabled={!speakerUrl || !participantUrl}
  >
    Download Speaker PDF
  </button>
  {/* <button
    className={styles.downloadShareBtn}
    onClick={() => onSharePdf("speaker")}
    disabled={!speakerUrl || !participantUrl}
  >
    Share Speaker PDF
  </button> */}

  <button
    className={styles.downloadShareBtn}
    onClick={() => onDownloadPdf("listener")}
    disabled={!speakerUrl || !participantUrl}
  >
    Download Listener PDF
  </button>
  {/* <button
    className={styles.downloadShareBtn}
    onClick={() => onSharePdf("listener")}
    disabled={!speakerUrl || !participantUrl}
  >
    Share Listener PDF
  </button> */}
</div>

          </div>

          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading room data...</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <p>Error: {error}</p>
            </div>
          ) : currentRoom ? (
            <div className={styles.infoSectionsContainer}>
              {renderInfoSection("Speaker")}
              {renderInfoSection("Listener")}
            </div>
          ) : (
            <div className={styles.errorContainer}>
              <p>
                Room data not found. Please select a room or create a new one.
              </p>
              <button
                onClick={onRoomsListClick}
                className={styles.actionButton}
                style={{
                  backgroundColor: "var(--color-seagreen-100)",
                  marginTop: "15px",
                }}
              >
                Go to Rooms List
              </button>
            </div>
          )}
        </div>
      </div>
    

    </div>
  );
};

export default JoinInfoScreen;
